import {
  CloseCircleIcon,
  CurrencyIcon,
  DepositIcon,
  EditIcon,
  ExchangeIcon,
  GearIcon,
  MMButton,
  PayoutIcon,
  ProCard,
  WithdrawIcon
} from '@components'
import menuStyles from '@components/Layout/HeaderMenuSurface/HeaderMenuSurface.module.scss'
import cn from '@integrations/classnames'
import { useEffect } from '@integrations/react'
import { uiSize } from '@utils'
import { useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { formatMoney, tr, useDropDownMenu } from 'mmfintech-commons'
import AccountPaymentDetails from '../AccountPaymentDetails'
import AccountTransactions from '../AccountTransactions'
import styles from './AccountWorkspace.module.scss'

type Action = (() => void) | null

interface Props {
  account: AccountBalanceResponse
  onViewTransactions: () => void
  onRename: Action
  /** Opens the close-account warning; owners and administrators only. */
  onCloseAccount: Action
  onDeposit: Action
  onWithdraw: Action
  onExchange: Action
  onPayout: Action
  /** Set when the selected account can still get a named IBAN. */
  onRequestIban: Action
  /** The account's currency can have an IBAN issued; otherwise the IBAN tile is hidden (unless it already has one). */
  ibanSupported: boolean
}

/** Everything about the selected account, once: balance and money actions, then payment details. */
function AccountWorkspace({
  account,
  onViewTransactions,
  onRename,
  onCloseAccount,
  onDeposit,
  onWithdraw,
  onExchange,
  onPayout,
  onRequestIban,
  ibanSupported
}: Props) {
  const { id, name, currencyCode, balance } = account
  const { getCurrencyPrecision } = useCurrencies()

  // Click outside and Esc close the menu via the hook; picking another account closes it too
  const settingsMenu = useDropDownMenu()
  const { setVisible: setMenuVisible } = settingsMenu
  // biome-ignore lint/correctness/useExhaustiveDependencies: `id` is the trigger — close on account switch
  useEffect(() => {
    setMenuVisible(false)
  }, [id, setMenuVisible])

  function handleMenuAction(action: () => void) {
    setMenuVisible(false)
    action()
  }

  const actions = [
    {
      key: 'deposit',
      handler: onDeposit,
      icon: DepositIcon,
      caption: tr('FRONTEND.DASHBOARD.BUTTON_DEPOSIT', 'Deposit')
    },
    {
      key: 'withdraw',
      handler: onWithdraw,
      icon: WithdrawIcon,
      caption: tr('FRONTEND.DASHBOARD.BUTTON_WITHDRAW', 'Withdraw')
    },
    {
      key: 'exchange',
      handler: onExchange,
      icon: ExchangeIcon,
      caption: tr('FRONTEND.DASHBOARD.BUTTON_EXCHANGE', 'Exchange')
    },
    { key: 'payout', handler: onPayout, icon: PayoutIcon, caption: tr('FRONTEND.DASHBOARD.BUTTON_PAYOUT', 'Payout') }
  ].filter((action) => action.handler)

  return (
    <ProCard className={cn('account-workspace')} data-test='account-workspace'>
      <ProCard tone='brand' className={cn('account-hero')}>
        <div className={cn(styles['account-hero__head'], 'account-hero__head')}>
          <CurrencyIcon
            currency={currencyCode}
            size={uiSize(48)}
            circle
            className={cn(styles['account-hero__icon'], 'account-hero__icon')}
          />
          <div className={cn(styles['account-hero__title'], 'account-hero__title')}>
            <div className={cn(styles['account-hero__name'], 'account-hero__name')} data-test='account-name'>
              {name}
            </div>
            <div className={cn(styles['account-hero__sub'], 'account-hero__sub')}>
              {tr('FRONTEND.ACCOUNTS.WORKSPACE.CURRENCY_ACCOUNT', '{{CURRENCY}} account', { CURRENCY: currencyCode })}
            </div>
          </div>
          {/* One group, so the gear can never wrap away from the button it belongs next to */}
          <div className={cn(styles['account-hero__tools'], 'account-hero__tools')} data-test='account-hero-tools'>
            <MMButton
              variant='secondary'
              caption={tr('FRONTEND.ACCOUNTS.COLUMNS.VIEW_TRANSACTIONS', 'View transactions')}
              onClick={onViewTransactions}
              data-test='transactions-button'
            />
            {(onRename || onCloseAccount) && (
              <div className={cn(menuStyles['header-menu-wrapper'], 'account-settings-menu')} ref={settingsMenu.setRef}>
                <MMButton
                  variant='secondary'
                  collapsed
                  caption={tr('FRONTEND.ACCOUNTS.SETTINGS.TITLE', 'Account settings')}
                  title={tr('FRONTEND.ACCOUNTS.SETTINGS.TITLE', 'Account settings')}
                  collapsedIcon={<GearIcon />}
                  aria-haspopup='menu'
                  aria-expanded={settingsMenu.visible}
                  onClick={settingsMenu.toggleDropDown}
                  data-test='account-settings-button'
                />
                {settingsMenu.visible && (
                  <div
                    role='menu'
                    aria-label={tr('FRONTEND.ACCOUNTS.SETTINGS.TITLE', 'Account settings')}
                    className={cn(menuStyles['header-menu-dropdown'], menuStyles['opened'])}
                    data-test='account-settings-menu'
                  >
                    {onRename && (
                      <button
                        type='button'
                        role='menuitem'
                        className={cn(menuStyles['header-menu-option'])}
                        onClick={() => handleMenuAction(onRename)}
                        data-test='edit-button'
                      >
                        <EditIcon />
                        {tr('FRONTEND.ACCOUNTS.SETTINGS.EDIT_NAME', 'Edit account name')}
                      </button>
                    )}
                    {onRename && onCloseAccount && <div className={cn(menuStyles['header-menu-divider'])} />}
                    {onCloseAccount && (
                      <button
                        type='button'
                        role='menuitem'
                        className={cn(menuStyles['header-menu-option'], menuStyles['danger'])}
                        onClick={() => handleMenuAction(onCloseAccount)}
                        data-test='close-account-button'
                      >
                        <CloseCircleIcon />
                        {tr('FRONTEND.ACCOUNTS.CLOSE.BUTTON', 'Close account')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className={cn(styles['account-hero__label'], 'account-hero__label')}>
            <span className={cn(styles['account-hero__swatch'], 'account-hero__swatch')} />
            {tr('FRONTEND.ACCOUNTS.COLUMNS.BALANCE', 'Balance')}
          </div>
          <div className={cn(styles['account-hero__balance'], 'account-hero__balance')} data-test='balance'>
            {formatMoney(balance, '', getCurrencyPrecision(currencyCode)).trim()}
            <span className={cn(styles['account-hero__currency'], 'account-hero__currency')}>{currencyCode}</span>
          </div>
        </div>

        {actions.length > 0 && (
          <div className={cn(styles['account-hero__actions'], 'account-hero__actions')}>
            {actions.map(({ key, handler, icon, caption }) => (
              <MMButton
                key={key}
                variant={key === 'deposit' ? 'primary' : 'secondary'}
                leftIcon={icon}
                caption={caption}
                onClick={handler}
                data-test={`button-${key}`}
              />
            ))}
          </div>
        )}
      </ProCard>

      <section className={cn(styles['account-details'], 'account-details')}>
        <h3 className={cn(styles['account-details__title'], 'account-details__title')}>
          {tr('FRONTEND.ACCOUNTS.WORKSPACE.PAYMENT_DETAILS', 'Payment details')}
        </h3>
        {/* Keyed by account so a "Copied!" mark or download error never carries over to the next account */}
        <AccountPaymentDetails key={id} account={account} onRequestIban={onRequestIban} ibanSupported={ibanSupported} />
      </section>

      <section className={cn(styles['account-details'], 'account-details')}>
        <AccountTransactions
          accountId={id}
          heading={
            <h3 className={cn(styles['account-details__title'], 'account-details__title')}>
              {tr('FRONTEND.ACCOUNTS.WORKSPACE.LATEST_TRANSACTIONS', 'Last 10 transactions')}
            </h3>
          }
        />
      </section>
    </ProCard>
  )
}

export default AccountWorkspace
